import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useSEO } from '../../hooks/useSEO';
import { propertiesApi, citiesApi, articlesApi, seedApi, wilayasApi, formatPrice } from '../../lib/api';
import { PropertyCard } from '../../components/property/PropertyCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Search, MapPin, Home, Building2, TrendingUp, ArrowRight, ChevronRight, BookOpen, Key, Banknote, Plus } from 'lucide-react';

export const HomePage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [cities, setCities] = useState([]); // For display (5 main cities)
  const [wilayas, setWilayas] = useState([]); // For search (all 48 wilayas)
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('vente');
  const [cityInput, setCityInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const normalize = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  const filteredWilayas = wilayas.filter(w => {
    const q = normalize(cityInput);
    return normalize(w.name).includes(q) || (w.name_ar && w.name_ar.includes(cityInput));
  }).slice(0, 8);

  useEffect(() => {
    const loadData = async () => {
      try {
        await seedApi.seed().catch(() => {});
        
        const [propsRes, citiesRes, articlesRes, wilayasRes] = await Promise.all([
          propertiesApi.list({ limit: 6 }),
          citiesApi.list(),
          articlesApi.list({ limit: 3 }),
          wilayasApi.list(),
        ]);
        
        setProperties(propsRes.data.properties || []);
        setCities(citiesRes.data || []);
        setArticles(articlesRes.data || []);
        setWilayas(wilayasRes.data || []);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set('transaction_type', searchType);
    if (searchQuery) params.set('city', searchQuery);
    navigate(`/search?${params.toString()}`);
  };

  const cityImages = {
    'Alger': 'https://images.unsplash.com/photo-1675610137184-40f1bce58d71?w=800',
    'Oran': 'https://images.unsplash.com/photo-1706002027900-a1be40d0627e?w=800',
    'Béjaïa': 'https://images.unsplash.com/photo-1630838788292-d9c78b6a0f2f?w=800',
    'Tizi Ouzou': 'https://images.unsplash.com/photo-1605692024639-04377ef2fd90?w=800',
    'Annaba': 'https://images.unsplash.com/photo-1760726743931-24bd760fe554?w=800',
  };

  const guides = [
    {
      icon: Key,
      title: t('guide_buyer'),
      desc: t('guide_buyer_desc'),
      link: '/blog',
      color: 'bg-blue-50 text-blue-900',
    },
    {
      icon: Home,
      title: t('guide_renter'),
      desc: t('guide_renter_desc'),
      link: '/blog',
      color: 'bg-emerald-50 text-emerald-700',
    },
    {
      icon: Banknote,
      title: t('guide_seller'),
      desc: t('guide_seller_desc'),
      link: '/blog',
      color: 'bg-rose-50 text-rose-700',
    },
  ];

  const seoTitle = language === 'fr' ? 'Beytam - Immobilier en Algerie | Achat, Vente, Location' : 'بيتم - العقارات في الجزائر | شراء، بيع، إيجار';
  const seoDesc = language === 'fr' ? "Beytam, le portail immobilier de reference en Algerie. Trouvez votre bien parmi des milliers d'annonces dans les 48 wilayas." : 'بيتم، البوابة العقارية المرجعية في الجزائر. اعثر على عقارك بين آلاف الإعلانات في 48 ولاية.';

  useSEO({
    title: seoTitle,
    description: seoDesc,
    ogTitle: 'Beytam - Immobilier en Algerie',
    ogDescription: "Trouvez votre bien immobilier parmi des milliers d'annonces en Algerie.",
    canonical: window.location.origin,
  });

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* Hero Section - SeLoger Style */}
      <section className="relative h-[500px] md:h-[550px] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/11720558/pexels-photo-11720558.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260"
          alt="Hero"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/10" />
        
        <div className="relative h-full flex flex-col items-center justify-center px-4">
          <div className="text-center mb-8 animate-fade-in-up">
            <h1 className="font-manrope text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-4">
              {t('hero_title')}
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-6">
              {t('hero_subtitle')}
            </p>
            {/* CTA Déposer une annonce */}
            <Link to="/add-property">
              <Button size="lg" className="bg-rose-600 hover:bg-rose-700 text-white rounded-full px-8 py-6 text-base font-semibold shadow-xl hover:shadow-2xl transition-all">
                <Plus className="w-5 h-5 mr-2" />
                {t('cta_publish')}
              </Button>
            </Link>
          </div>

          {/* Search Widget - SeLoger Style */}
          <div className="w-full max-w-3xl animate-slide-up stagger-2" data-testid="hero-search">
            {/* Tabs */}
            <div className="flex justify-center mb-4">
              <div className="inline-flex bg-white/20 backdrop-blur-sm rounded-full p-1">
                <button
                  type="button"
                  className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                    searchType === 'vente' 
                      ? 'bg-white text-blue-900 shadow-md' 
                      : 'text-white hover:bg-white/10'
                  }`}
                  onClick={() => setSearchType('vente')}
                  data-testid="search-type-vente"
                >
                  {t('search_buy')}
                </button>
                <button
                  type="button"
                  className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                    searchType === 'location' 
                      ? 'bg-white text-blue-900 shadow-md' 
                      : 'text-white hover:bg-white/10'
                  }`}
                  onClick={() => setSearchType('location')}
                  data-testid="search-type-location"
                >
                  {t('search_rent')}
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="flex-grow relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10" />
                <Input
                  type="text"
                  value={cityInput}
                  onChange={(e) => {
                    setCityInput(e.target.value);
                    setSearchQuery(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder={language === 'fr' ? 'Tapez une ville ou wilaya...' : 'اكتب اسم مدينة أو ولاية...'}
                  className="w-full h-14 pl-12 pr-4 rounded-full bg-white border-0 shadow-xl text-base focus-visible:ring-2 focus-visible:ring-rose-400"
                  data-testid="search-city-input"
                />
                {showSuggestions && cityInput.length > 0 && filteredWilayas.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50" data-testid="city-suggestions">
                    {filteredWilayas.map((wilaya) => (
                      <button
                        key={wilaya.name}
                        type="button"
                        className="w-full px-5 py-3 text-left text-sm hover:bg-blue-50 transition-colors flex items-center gap-3 border-b border-slate-50 last:border-0"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setCityInput(wilaya.name);
                          setSearchQuery(wilaya.name);
                          setShowSuggestions(false);
                        }}
                        data-testid={`suggestion-${wilaya.name}`}
                      >
                        <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <span className="text-slate-800 font-medium">{wilaya.name}</span>
                        {wilaya.name_ar && <span className="text-slate-400 text-xs ml-auto" dir="rtl">{wilaya.name_ar}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button 
                type="submit" 
                className="h-14 px-8 rounded-full bg-rose-600 hover:bg-rose-700 text-white shadow-xl"
                data-testid="search-submit-btn"
              >
                <Search className="w-5 h-5 md:mr-2" />
                <span className="hidden md:inline">{t('search_btn')}</span>
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Guides Section - SeLoger Style */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-manrope text-2xl md:text-3xl font-bold text-slate-900">
              {language === 'fr' ? 'Beytam vous accompagne' : 'بيتم يرافقك'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {guides.map((guide, index) => (
              <Link key={index} to={guide.link}>
                <Card className="h-full border-0 shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_12px_24px_rgba(0,0,0,0.12)] transition-all duration-300 group">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl ${guide.color} flex items-center justify-center flex-shrink-0`}>
                      <guide.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-manrope font-bold text-lg text-slate-900 mb-1 group-hover:text-blue-900 transition-colors">
                        {guide.title}
                      </h3>
                      <p className="text-sm text-slate-600">{guide.desc}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-900 transition-colors ml-auto flex-shrink-0" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Cities Bento Grid - SeLoger Style */}
      <section className="py-16 md:py-20" data-testid="cities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="font-manrope text-2xl md:text-3xl font-bold text-slate-900">
                {t('cities_title')}
              </h2>
              <p className="text-slate-600 mt-1">{t('cities_subtitle')}</p>
            </div>
            <Link to="/cities" className="hidden md:block">
              <Button variant="outline" className="rounded-full gap-2 hover:bg-blue-50 hover:text-blue-900 hover:border-blue-200">
                {t('common_see_all')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:auto-rows-[minmax(180px,auto)]">
            {cities.slice(0, 5).map((city, index) => {
              const spans = index === 0 ? 'md:col-span-2 md:row-span-2' : 'md:col-span-1';
              
              return (
                <Link
                  key={city.city}
                  to={`/cities/${city.city.toLowerCase()}`}
                  className={`${spans} group relative rounded-2xl overflow-hidden`}
                  data-testid={`city-card-${city.city.toLowerCase()}`}
                >
                  <img
                    src={cityImages[city.city] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800'}
                    alt={city.city}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <h3 className="font-manrope font-bold text-xl mb-1">{city.city}</h3>
                    <div className="flex items-center gap-3 text-sm text-white/80">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {city.total_listings} {t('cities_listings')}
                      </span>
                      {index === 0 && (
                        <span className="flex items-center gap-1">
                          <TrendingUp className="w-4 h-4" />
                          {formatPrice(city.avg_price_sqm_sale)}/m²
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-8 md:hidden">
            <Link to="/cities">
              <Button variant="outline" className="rounded-full gap-2">
                {t('common_see_all')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Price Map CTA */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-8 bg-white rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
            <div className="md:w-1/2">
              <img 
                src="https://images.unsplash.com/photo-1725042893312-5ec0dea9e369?w=800" 
                alt="Price Map"
                className="rounded-xl w-full h-64 object-cover"
              />
            </div>
            <div className="md:w-1/2 space-y-4">
              <span className="text-rose-600 font-semibold text-sm">
                {language === 'fr' ? 'Restez informé !' : 'ابق على اطلاع!'}
              </span>
              <h3 className="font-manrope text-2xl md:text-3xl font-bold text-slate-900">
                {language === 'fr' ? 'Découvrez les prix de l\'immobilier en Algérie' : 'اكتشف أسعار العقارات في الجزائر'}
              </h3>
              <p className="text-slate-600">
                {language === 'fr' 
                  ? 'Utilisez la carte des prix de Beytam pour obtenir des informations sur le marché. Prix au m² par ville et quartier.'
                  : 'استخدم خريطة أسعار بيتم للحصول على معلومات عن السوق. سعر المتر المربع حسب المدينة والحي.'}
              </p>
              <Link to="/cities">
                <Button className="bg-blue-900 hover:bg-blue-800 rounded-full px-6 gap-2">
                  {language === 'fr' ? 'Explorer la carte des prix' : 'استكشف خريطة الأسعار'}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Properties */}
      <section className="py-16 md:py-20" data-testid="latest-properties">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="font-manrope text-2xl md:text-3xl font-bold text-slate-900">
                {language === 'fr' ? 'Dernières annonces' : 'أحدث الإعلانات'}
              </h2>
              <p className="text-slate-600 mt-1">
                {language === 'fr' ? 'Les biens récemment ajoutés' : 'العقارات المضافة حديثاً'}
              </p>
            </div>
            <Link to="/search" className="hidden md:block">
              <Button variant="outline" className="rounded-full gap-2 hover:bg-blue-50 hover:text-blue-900 hover:border-blue-200">
                {t('common_see_all')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="animate-pulse border-0 shadow-sm">
                  <div className="aspect-[4/3] bg-slate-200" />
                  <CardContent className="p-4 space-y-3">
                    <div className="h-6 bg-slate-200 rounded w-1/2" />
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          )}

          <div className="text-center mt-8 md:hidden">
            <Link to="/search">
              <Button variant="outline" className="rounded-full gap-2">
                {t('common_see_all')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Publish CTA - SeLoger Style */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-8 bg-white rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
            <div className="md:w-1/2 order-2 md:order-1 space-y-4">
              <span className="inline-block bg-blue-100 text-blue-900 px-3 py-1 rounded-full text-sm font-semibold">
                {language === 'fr' ? 'Propriétaire ?' : 'مالك؟'}
              </span>
              <h3 className="font-manrope text-2xl md:text-3xl font-bold text-slate-900">
                {t('cta_publish_subtitle')}
              </h3>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-900 text-xs">✓</span>
                  </div>
                  {language === 'fr' ? 'Présentez votre bien et ses caractéristiques' : 'قدم عقارك ومميزاته'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-900 text-xs">✓</span>
                  </div>
                  {language === 'fr' ? 'Définissez le prix de vente' : 'حدد سعر البيع'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-900 text-xs">✓</span>
                  </div>
                  {language === 'fr' ? 'Mettez en avant ce qui le rend unique' : 'أبرز ما يجعله فريداً'}
                </li>
              </ul>
              <Link to="/add-property">
                <Button className="bg-rose-600 hover:bg-rose-700 rounded-full px-6 gap-2">
                  {t('cta_publish')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="md:w-1/2 order-1 md:order-2">
              <img 
                src="https://images.unsplash.com/photo-1758523671819-06a0f1941520?w=800" 
                alt="Publish"
                className="rounded-xl w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      {articles.length > 0 && (
        <section className="py-16 md:py-20" data-testid="blog-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="font-manrope text-2xl md:text-3xl font-bold text-slate-900">
                  {language === 'fr' ? 'Les dernières nouveautés' : 'آخر الأخبار'}
                </h2>
              </div>
              <Link to="/blog" className="hidden md:block">
                <Button variant="outline" className="rounded-full gap-2 hover:bg-blue-50 hover:text-blue-900 hover:border-blue-200">
                  {t('blog_all')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link key={article.id} to={`/blog/${article.slug}`}>
                  <Card className="overflow-hidden border-0 shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_12px_24px_rgba(0,0,0,0.12)] transition-all duration-300 group h-full">
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={article.image || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800'}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <CardContent className="p-5">
                      <span className="text-rose-600 text-xs font-semibold uppercase tracking-wide">
                        {article.category}
                      </span>
                      <h3 className="font-semibold text-slate-900 mt-2 line-clamp-2 group-hover:text-blue-900 transition-colors">
                        {language === 'ar' && article.title_ar ? article.title_ar : article.title}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
